import type { LiveSession } from "@/lib/odds-screen/session";
import { phCapture } from "@/utils/analytics";
import { noteFirstLiveRow, noteLiveSnapshot } from "@/lib/performance/first-live-row";
import { takeEarlySocket } from "./early-socket";
import { captureStreamError, invalidMessageTags, rejectedRowsExtra } from "./report";
import { INPLAY_EVENT_TOPICS } from "./inplay";
import {
  encodeCommand, isValidTopic, oddsScreenSessionSchema, parseServerMessage, ProtocolError,
  type ClientCommand, type ServerMessage, type TopicDeltaPayload, type TopicPayload,
} from "./protocol";
import {
  applyArbitrageDelta, applyBoardDelta, applyCatalogDelta, applyDfsDelta, applyDiscrepanciesDelta, applyInplayEventDelta, applyInplayListDelta, applyInplayListSnap, applyLadderDelta, applyLowHoldDelta, applyPropsDelta, applySniperDelta, applyWhaleRowDelta, boardView, emptyTopicState, applyRaceDelta,
  propsView, reduceTopic, reduceWhaleMessages, type EventRows, type EventRowsDelta, type Reduction, type ResyncCause, type ViewRow,
} from "./store";
import {
  topicId,
  type Arbitrage, type ArbitrageTopic, type ArbitrageView, type Board, type BoardTopic, type BoardView, type Catalog, type CatalogTopic, type ConnectionStatus, type Dfs, type DfsTopic, type EndReason,
  type BookRowsTopic, type Discrepancies, type DiscrepanciesTopic, type DiscrepanciesView, type EventRowsView, type InplayEvent, type InplayEventTopic, type InplayList, type InplayStatus, type InplayTopic, type Ladder, type LadderTopic, type LeagueSummary, type LowHold, type LowHoldTopic, type LowHoldView, type Props, type PropsView, type RaceList, type RaceTopic, type RowId, type Sniper, type SniperRowsTopic, type SniperTopic, type SniperView, type Topic, type TopicPhase, type TopicState,
  type TopicValue, type WhaleDetail, type WhaleList, type WhaleRowTopic, type WhaleTopic, type WhalesLadder, type WhalesTopic,
} from "./types";

export type HeldBoard = { readonly topic: BoardTopic; readonly view: BoardView };

export interface OddsLiveClient {
  retain(topic: Topic): () => void;
  prefetch(topic: Topic): void;
  read<T extends Topic>(topic: T): TopicState<TopicValue<T>>;
  readBoardView(topic: BoardTopic): BoardView;
  readPropsView(topic: BookRowsTopic): PropsView;
  readDiscrepanciesView(topic: DiscrepanciesTopic): DiscrepanciesView;
  readArbitrageView(topic: ArbitrageTopic): ArbitrageView;
  readLowHoldView(topic: LowHoldTopic): LowHoldView;
  readSniperView(topic: SniperRowsTopic): SniperView;
  listBoards(): readonly HeldBoard[];
  listen(topic: Topic, onChange: () => void): () => void;
  prime(session: LiveSession): void;
  status(): ConnectionStatus;
  listenStatus(onChange: () => void): () => void;
  report(reason: string, topic: Topic | null, extra?: Record<string, unknown>): void;
}

type Timer = ReturnType<typeof setTimeout>;

type EntryBase = {
  readonly id: string;
  refs: number;
  linger: Timer | undefined;
  used: number;
  subscribed: boolean;
  retried: boolean;
};
type CatalogEntry = EntryBase & { readonly kind: "catalog"; readonly topic: CatalogTopic; state: TopicState<Catalog> };
type DfsEntry = EntryBase & { readonly kind: "dfs"; readonly topic: DfsTopic; state: TopicState<Dfs> };
type BoardEntry = EntryBase & { readonly kind: "board"; readonly topic: BoardTopic; state: TopicState<Board>; view: BoardView };
type LadderEntry = EntryBase & { readonly kind: "ladder"; readonly topic: LadderTopic; state: TopicState<Ladder> };
type PropsEntry = EntryBase & { readonly kind: BookRowsTopic["kind"]; readonly topic: BookRowsTopic; state: TopicState<Props>; view: PropsView };
type WhaleEntry = EntryBase & { readonly kind: "whale"; readonly topic: WhaleTopic; state: TopicState<WhaleList>; queue: TopicMessage[] };
type WhaleRowEntry = EntryBase & { readonly kind: "whale_row"; readonly topic: WhaleRowTopic; state: TopicState<WhaleDetail> };
type DiscrepanciesEntry = EntryBase & { readonly kind: "discrepancies"; readonly topic: DiscrepanciesTopic; state: TopicState<Discrepancies>; view: DiscrepanciesView };
type ArbitrageEntry = EntryBase & { readonly kind: "arbitrage"; readonly topic: ArbitrageTopic; state: TopicState<Arbitrage>; view: ArbitrageView };
type LowHoldEntry = EntryBase & { readonly kind: "lowhold"; readonly topic: LowHoldTopic; state: TopicState<LowHold>; view: LowHoldView };
type SniperEntry = EntryBase & { readonly kind: SniperRowsTopic["kind"]; readonly topic: SniperRowsTopic; state: TopicState<Sniper>; view: SniperView };
type RaceEntry = EntryBase & { readonly kind: "race"; readonly topic: RaceTopic; state: TopicState<RaceList> };
type InplayEntry = EntryBase & { readonly kind: "live"; readonly topic: InplayTopic; state: TopicState<InplayList> };
type InplayEventEntry = EntryBase & { readonly kind: "live_event"; readonly topic: InplayEventTopic; state: TopicState<InplayEvent> };
type WhalesEntry = EntryBase & { readonly kind: "whales"; readonly topic: WhalesTopic; state: TopicState<WhalesLadder> };
type Entry = CatalogEntry | DfsEntry | BoardEntry | LadderEntry | PropsEntry | WhaleEntry | WhaleRowEntry | DiscrepanciesEntry | ArbitrageEntry | LowHoldEntry | SniperEntry | RaceEntry | InplayEntry | InplayEventEntry | WhalesEntry;

type Connection = {
  socket: WebSocket | null;
  authed: boolean;
  fatal: string | null;
  expiresAt: number;
  watchdog: Timer | undefined;
  renewal: Timer | undefined;
  stable: Timer | undefined;
  clockOffsetMs: number;
  tokens: number;
  refilledAt: number | null;
  outbox: ClientCommand[];
  pump: Timer | undefined;
  received: number;
  ack: Timer | undefined;
  acked: boolean;
};

type Session = { readonly ticket: string; readonly streamUrl: string };
type PrimedSession = Session & { readonly expiresAtMs: number };
const PRIMED_MIN_LIFE_MS = 5_000;
type TopicMessage = Extract<ServerMessage, { type: "snap" | "delta" }>;

const CAP_GROUP = { dfs: "dfs", board: "board", ladder: "ladder", props: "props", optimizer: "optimizer", whale: "whale", whale_row: "whale", discrepancies: "discrepancies", arbitrage: "arbitrage", lowhold: "arbitrage", sniper: "sniper", sniper_live: "sniper_live", race: "race", live: "live", live_event: "live_event", whales: "whales" } as const;
type CapGroup = (typeof CAP_GROUP)[keyof typeof CAP_GROUP];
const LIMITS: Record<CapGroup, number> = { dfs: 1, board: 4, ladder: 8, props: 16, optimizer: 16, whale: 8, discrepancies: 16, arbitrage: 32, sniper: 32, sniper_live: 32, race: 2, live: 2, live_event: INPLAY_EVENT_TOPICS, whales: 4 };
const LINGER_MS = { catalog: 60_000, dfs: 60_000, board: 60_000, ladder: 15_000, props: 60_000, optimizer: 60_000, whale: 30_000, whale_row: 0, discrepancies: 60_000, arbitrage: 60_000, lowhold: 60_000, sniper: 60_000, sniper_live: 60_000, race: 60_000, live: 60_000, live_event: 15_000, whales: 0 } as const;
const REVIVABLE: ReadonlySet<EndReason | null> = new Set<EndReason>(["row_removed", "unknown_topic"]);
const HIDDEN_CLOSE_MS = 60_000;
const WATCHDOG_MS = 45_000;
const ACK_MS = 200;
const RENEW_BEFORE_MS = 60_000;
const RENEW_RETRY_MS = 10_000;
const STABLE_MS = 30_000;
const BACKOFF_BASE_MS = 500;
const BACKOFF_CAP_MS = 30_000;
const SHUTDOWN_JITTER_MS = 5_000;
const REPORT_THROTTLE_MS = 60_000;
const RESYNC_REPORTS_PER_WINDOW = 5;
const REPEATED_RECONNECT_THRESHOLD = 6;
const COMMAND_BURST = 30;
const COMMAND_REFILL_MS = 50;
const SESSION_PATH = "/api/odds-screen/session";
const FANTASY_SESSION_PATH = "/api/fantasy-props/session";
const OPTIMIZER_SESSION_PATH = "/api/optimizer/session";
const DISCREPANCIES_SESSION_PATH = "/api/prop-discrepancies/session";
const ARBITRAGE_SESSION_PATH = "/api/arbitrage/session";
const LOW_HOLD_SESSION_PATH = "/api/low-hold/session";
const WHALE_SESSION_PATH = "/api/whale-watch/session";
const SNIPER_SESSION_PATH = "/api/ev-sniper/session";

const CLOSE_CODE_REASON: Readonly<Record<number, string>> = {
  4013: "slow_consumer",
  4029: "rate_limited",
  4030: "subject_limit",
  4400: "command_invalid",
};

const EMPTY_STATE = emptyTopicState<never>();
const EMPTY_VIEW = boardView(null, EMPTY_STATE);
const EMPTY_PROPS_VIEW: PropsView = propsView(null, EMPTY_STATE);
const EMPTY_DISCREPANCIES_VIEW: DiscrepanciesView = propsView(null, emptyTopicState<Discrepancies>());
const EMPTY_ARBITRAGE_VIEW: ArbitrageView = propsView(null, emptyTopicState<Arbitrage>());
const EMPTY_LOWHOLD_VIEW: LowHoldView = propsView(null, emptyTopicState<LowHold>());
const EMPTY_SNIPER_VIEW: SniperView = propsView(null, emptyTopicState<Sniper>());

function mismatch(): never {
  throw new ProtocolError("Payload kind does not match its topic");
}

const loggedSkips = new Set<string>();

function logSkippedFamilies(leagues: Iterable<LeagueSummary>): void {
  for (const league of leagues) {
    for (const family of league.skippedFamilies) {
      const key = `${league.id}|${family.id}|${family.layout}`;
      if (loggedSkips.has(key)) continue;
      loggedSkips.add(key);
      phCapture("odds_screen_family_skipped", { league: league.id, family: family.id, layout: family.layout });
    }
  }
}

const catalogSnap = (payload: TopicPayload): Catalog => {
  if (payload.kind !== "catalog") mismatch();
  logSkippedFamilies(payload.snap.leagues.values());
  return payload.snap;
};
const catalogDelta = (value: Catalog, payload: TopicDeltaPayload): Catalog => {
  if (payload.kind !== "catalog") mismatch();
  logSkippedFamilies(payload.delta.leagues);
  return applyCatalogDelta(value, payload.delta);
};
const dfsSnap = (payload: TopicPayload): Dfs => (payload.kind === "dfs" ? payload.snap : mismatch());
const dfsDelta = (value: Dfs, payload: TopicDeltaPayload): Dfs => (payload.kind === "dfs" ? applyDfsDelta(value, payload.delta) : mismatch());
const boardSnap = (payload: TopicPayload): Board => (payload.kind === "board" ? payload.snap : mismatch());
const boardDelta = (value: Board, payload: TopicDeltaPayload): Board =>
  payload.kind === "board" ? applyBoardDelta(value, payload.delta) : mismatch();
const ladderSnap = (payload: TopicPayload): Ladder => (payload.kind === "ladder" ? payload.snap : mismatch());
const ladderDelta = (value: Ladder, payload: TopicDeltaPayload): Ladder =>
  payload.kind === "ladder" ? applyLadderDelta(value, payload.delta) : mismatch();
const propsSnap = (payload: TopicPayload): Props => (payload.kind === "props" ? payload.snap : mismatch());
const propsDeltaOf = (payload: TopicDeltaPayload) => (payload.kind === "props" ? payload.delta : null);
const discrepanciesSnap = (payload: TopicPayload): Discrepancies => (payload.kind === "discrepancies" ? payload.snap : mismatch());
const discrepanciesDeltaOf = (payload: TopicDeltaPayload) => (payload.kind === "discrepancies" ? payload.delta : null);
const arbitrageSnap = (payload: TopicPayload): Arbitrage => (payload.kind === "arbitrage" ? payload.snap : mismatch());
const arbitrageDeltaOf = (payload: TopicDeltaPayload) => (payload.kind === "arbitrage" ? payload.delta : null);
const lowHoldSnap = (payload: TopicPayload): LowHold => (payload.kind === "lowhold" ? payload.snap : mismatch());
const lowHoldDeltaOf = (payload: TopicDeltaPayload) => (payload.kind === "lowhold" ? payload.delta : null);
const sniperSnap = (payload: TopicPayload): Sniper => (payload.kind === "sniper" ? payload.snap : mismatch());
const sniperDeltaOf = (payload: TopicDeltaPayload) => (payload.kind === "sniper" ? payload.delta : null);
const raceSnap = (payload: TopicPayload): RaceList => (payload.kind === "race" ? payload.snap : mismatch());
const raceDelta = (value: RaceList, payload: TopicDeltaPayload): RaceList =>
  payload.kind === "race" ? applyRaceDelta(value, payload.delta) : mismatch();
const inplaySnap = (payload: TopicPayload, previous: InplayList | null): InplayList => (payload.kind === "live" ? applyInplayListSnap(previous, payload.snap) : mismatch());
const inplayDelta = (value: InplayList, payload: TopicDeltaPayload): InplayList =>
  payload.kind === "live" ? applyInplayListDelta(value, payload.delta) : mismatch();
const inplayEventSnap = (payload: TopicPayload): InplayEvent => (payload.kind === "live_event" ? payload.snap : mismatch());
const inplayEventDelta = (value: InplayEvent, payload: TopicDeltaPayload): InplayEvent =>
  payload.kind === "live_event" ? applyInplayEventDelta(value, payload.delta) : mismatch();
const whalesSnap = (payload: TopicPayload): WhalesLadder => (payload.kind === "whales" ? payload.snap : mismatch());
const whalesDelta = (_value: WhalesLadder, payload: TopicDeltaPayload): WhalesLadder => (payload.kind === "whales" ? payload.delta : mismatch());
const whaleRowSnap = (payload: TopicPayload): WhaleDetail => (payload.kind === "whale_row" ? payload.snap : mismatch());
const whaleRowDelta = (value: WhaleDetail, payload: TopicDeltaPayload): WhaleDetail =>
  payload.kind === "whale_row" ? applyWhaleRowDelta(value, payload.delta) : mismatch();

function withPhase<V>(state: TopicState<V>, phase: TopicPhase, ended: EndReason | null): TopicState<V> {
  return state.phase === phase && state.ended === ended ? state : { ...state, phase, ended };
}

function setPhase(entry: Entry, phase: TopicPhase, ended: EndReason | null): void {
  switch (entry.kind) {
    case "catalog":
      entry.state = withPhase(entry.state, phase, ended);
      return;
    case "dfs":
      entry.state = withPhase(entry.state, phase, ended);
      return;
    case "board":
      entry.state = withPhase(entry.state, phase, ended);
      entry.view = boardView(entry.view, entry.state);
      return;
    case "ladder":
      entry.state = withPhase(entry.state, phase, ended);
      return;
    case "props":
    case "optimizer":
      entry.state = withPhase(entry.state, phase, ended);
      entry.view = propsView(entry.view, entry.state);
      return;
    case "whale":
      entry.state = withPhase(entry.state, phase, ended);
      return;
    case "whale_row":
      entry.state = withPhase(entry.state, phase, ended);
      return;
    case "discrepancies":
      entry.state = withPhase(entry.state, phase, ended);
      entry.view = propsView(entry.view, entry.state);
      return;
    case "arbitrage":
      entry.state = withPhase(entry.state, phase, ended);
      entry.view = propsView(entry.view, entry.state);
      return;
    case "lowhold":
      entry.state = withPhase(entry.state, phase, ended);
      entry.view = propsView(entry.view, entry.state);
      return;
    case "sniper":
      entry.state = withPhase(entry.state, phase, ended);
      entry.view = propsView(entry.view, entry.state);
      return;
    case "sniper_live":
      entry.state = phase === "ended" ? { ...emptyTopicState<Sniper>(), phase, ended } : withPhase(entry.state, phase, ended);
      entry.view = propsView(entry.view, entry.state);
      return;
    case "race":
      entry.state = withPhase(entry.state, phase, ended);
      return;
    case "live":
      entry.state = withPhase(entry.state, phase, ended);
      return;
    case "live_event":
      entry.state = withPhase(entry.state, phase, ended);
      return;
    case "whales":
      entry.state = withPhase(entry.state, phase, ended);
      return;
  }
}

function onLocalClock(message: TopicMessage, offsetMs: number): TopicMessage {
  const local = (status: InplayStatus | null): InplayStatus | null => status === null ? null : {
    ...status,
    clock: status.clock === null ? null : { ...status.clock, atMs: status.clock.atMs + offsetMs },
    plays: status.plays.map((play) => ({ ...play, atMs: play.atMs + offsetMs })),
  };
  const srcMs = message.srcMs + offsetMs;
  if (message.type === "snap" && message.payload.kind === "live") {
    const games = new Map([...message.payload.snap.games].map(([id, game]) => [id, { ...game, status: local(game.status) }]));
    return { ...message, srcMs, payload: { kind: "live", snap: { ...message.payload.snap, games } } };
  }
  if (message.type === "delta" && message.payload.kind === "live") {
    const statuses = message.payload.delta.statuses.map(([id, status]) => [id, local(status)] as const);
    return { ...message, srcMs, payload: { kind: "live", delta: { ...message.payload.delta, statuses } } };
  }
  return { ...message, srcMs };
}

function reduceRows<R extends ViewRow, V extends EventRows<R>, D extends EventRowsDelta<R>>(
  entry: { state: TopicState<V>; view: EventRowsView<R> },
  message: TopicMessage,
  snap: (payload: TopicPayload) => V,
  deltaOf: (payload: TopicDeltaPayload) => D | null,
  apply: (value: V, delta: D) => V,
): Reduction<V> {
  const delta = message.type === "delta" ? deltaOf(message.payload) : null;
  const reduction = reduceTopic(entry.state, message, snap, (value) => (delta === null ? mismatch() : apply(value, delta)));
  if (reduction.kind !== "ignored") {
    entry.state = reduction.state;
    entry.view = propsView(entry.view, reduction.state, reduction.kind === "applied" ? delta : null);
  }
  return reduction;
}

export function reduceEntry(entry: Exclude<Entry, WhaleEntry>, message: TopicMessage): Reduction<unknown> {
  switch (entry.kind) {
    case "catalog": {
      const reduction = reduceTopic(entry.state, message, catalogSnap, catalogDelta);
      if (reduction.kind !== "ignored") entry.state = reduction.state;
      return reduction;
    }
    case "dfs": {
      const reduction = reduceTopic(entry.state, message, dfsSnap, dfsDelta);
      if (reduction.kind !== "ignored") entry.state = reduction.state;
      return reduction;
    }
    case "board": {
      const reduction = reduceTopic(entry.state, message, boardSnap, boardDelta);
      if (reduction.kind !== "ignored") {
        entry.state = reduction.state;
        entry.view = boardView(entry.view, reduction.state);
      }
      return reduction;
    }
    case "ladder": {
      const reduction = reduceTopic(entry.state, message, ladderSnap, ladderDelta);
      if (reduction.kind !== "ignored") entry.state = reduction.state;
      return reduction;
    }
    case "props":
    case "optimizer":
      return reduceRows(entry, message, propsSnap, propsDeltaOf, applyPropsDelta);
    case "whale_row": {
      const reduction = reduceTopic(entry.state, message, whaleRowSnap, whaleRowDelta);
      if (reduction.kind !== "ignored") entry.state = reduction.state;
      return reduction;
    }
    case "discrepancies":
      return reduceRows(entry, message, discrepanciesSnap, discrepanciesDeltaOf, applyDiscrepanciesDelta);
    case "arbitrage":
      return reduceRows(entry, message, arbitrageSnap, arbitrageDeltaOf, applyArbitrageDelta);
    case "lowhold":
      return reduceRows(entry, message, lowHoldSnap, lowHoldDeltaOf, applyLowHoldDelta);
    case "sniper":
    case "sniper_live":
      return reduceRows(entry, message, sniperSnap, sniperDeltaOf, applySniperDelta);
    case "race": {
      const reduction = reduceTopic(entry.state, message, raceSnap, raceDelta);
      if (reduction.kind !== "ignored") entry.state = reduction.state;
      return reduction;
    }
    case "live": {
      const reduction = reduceTopic(entry.state, message, inplaySnap, inplayDelta);
      if (reduction.kind !== "ignored") entry.state = reduction.state;
      return reduction;
    }
    case "live_event": {
      const reduction = reduceTopic(entry.state, message, inplayEventSnap, inplayEventDelta);
      if (reduction.kind !== "ignored") entry.state = reduction.state;
      return reduction;
    }
    case "whales": {
      const reduction = reduceTopic(entry.state, message, whalesSnap, whalesDelta);
      if (reduction.kind !== "ignored") entry.state = reduction.state;
      return reduction;
    }
  }
}

function listsRow(messages: readonly TopicMessage[], id: RowId): boolean {
  return messages.some((message) => {
    if (message.type === "snap") return message.payload.kind === "whale" && message.payload.snap.rows.has(id);
    return message.payload.kind === "whale" && message.payload.delta.rows.some((row) => row.market_data.id === id);
  });
}

export function createEntry(topic: Topic): Entry {
  const base: EntryBase = { id: topicId(topic), refs: 0, linger: undefined, used: 0, subscribed: false, retried: false };
  const initial = <V>(): TopicState<V> =>
    isValidTopic(topic) ? emptyTopicState<V>() : { ...emptyTopicState<V>(), phase: "ended", ended: "unknown_topic" };
  switch (topic.kind) {
    case "catalog": return { ...base, kind: "catalog", topic, state: initial<Catalog>() };
    case "dfs": return { ...base, kind: "dfs", topic, state: initial<Dfs>() };
    case "board": {
      const state = initial<Board>();
      return { ...base, kind: "board", topic, state, view: boardView(null, state) };
    }
    case "ladder": return { ...base, kind: "ladder", topic, state: initial<Ladder>() };
    case "props":
    case "optimizer": {
      const state = initial<Props>();
      return { ...base, kind: topic.kind, topic, state, view: propsView(null, state) };
    }
    case "whale": return { ...base, kind: "whale", topic, state: initial<WhaleList>(), queue: [] };
    case "whale_row": return { ...base, kind: "whale_row", topic, state: initial<WhaleDetail>() };
    case "discrepancies": {
      const state = initial<Discrepancies>();
      return { ...base, kind: "discrepancies", topic, state, view: propsView(null, state) };
    }
    case "arbitrage": {
      const state = initial<Arbitrage>();
      return { ...base, kind: "arbitrage", topic, state, view: propsView(null, state) };
    }
    case "lowhold": {
      const state = initial<LowHold>();
      return { ...base, kind: "lowhold", topic, state, view: propsView(null, state) };
    }
    case "sniper":
    case "sniper_live": {
      const state = initial<Sniper>();
      return { ...base, kind: topic.kind, topic, state, view: propsView(null, state) };
    }
    case "race": return { ...base, kind: "race", topic, state: initial<RaceList>() };
    case "live": return { ...base, kind: "live", topic, state: initial<InplayList>() };
    case "live_event": return { ...base, kind: "live_event", topic, state: initial<InplayEvent>() };
    case "whales": return { ...base, kind: "whales", topic, state: initial<WhalesLadder>() };
  }
}

function isHidden(): boolean {
  return typeof document !== "undefined" && document.hidden;
}

function isOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

async function mintSession(sessionPath: string): Promise<Session | "locked" | null> {
  try {
    const response = await fetch(sessionPath, { method: "POST", credentials: "same-origin", cache: "no-store", headers: { "X-Odds-Screen-Encoding": "compact" } });
    if (!response.ok) {
      await response.body?.cancel();
      return response.status === 401 || response.status === 403 ? "locked" : null;
    }
    const session = oddsScreenSessionSchema.parse(await response.json());
    return { ticket: session.ticket, streamUrl: session.stream_url };
  } catch {
    return null;
  }
}

export function createOddsLiveClient(sessionPath: string = SESSION_PATH): OddsLiveClient {
  const entries = new Map<string, Entry>();
  const listeners = new Map<string, Set<() => void>>();
  const statusListeners = new Set<() => void>();
  const dirty = new Set<string>();
  const queued = new Set<WhaleEntry>();
  let currentStatus: ConnectionStatus = "idle";
  let statusDirty = false;
  let flushScheduled = false;
  let connection: Connection | null = null;
  let reconnectTimer: Timer | undefined;
  let hiddenTimer: Timer | undefined;
  let attempt = 0;
  let locked = false;
  let limited = false;
  let rateLimited = false;
  let clock = 0;
  let domAttached = false;
  let primed: PrimedSession | null = null;
  const usedTickets = new Set<string>();
  const reportedAt = new Map<string, number>();
  let resyncWindowAt = -Infinity;
  let resyncReports = 0;
  let resyncSuppressed = 0;

  const report = (reason: string, topic: Topic | null, extra?: Record<string, unknown>, tags?: Record<string, string>) => {
    const now = Date.now();
    if (now - (reportedAt.get(reason) ?? -Infinity) < REPORT_THROTTLE_MS) return;
    reportedAt.set(reason, now);
    captureStreamError(reason, topic, extra, tags);
  };

  const reportResync = (topic: Topic, cause: ResyncCause) => {
    const now = Date.now();
    if (now - resyncWindowAt >= REPORT_THROTTLE_MS) {
      resyncWindowAt = now;
      resyncReports = 0;
    }
    if (resyncReports >= RESYNC_REPORTS_PER_WINDOW) {
      resyncSuppressed += 1;
      return;
    }
    resyncReports += 1;
    captureStreamError(
      "resync",
      topic,
      { held: cause.held, seq: cause.seq, from: cause.from, suppressed: resyncSuppressed },
      { odds_screen_resync: cause.reason, odds_screen_acked: String(connection?.acked === true) },
    );
    resyncSuppressed = 0;
  };

  const reportRejected = (message: TopicMessage) => {
    const extra = rejectedRowsExtra(message);
    if (extra !== null) report("rejected_rows", message.topic, extra);
  };

  const flush = () => {
    for (const entry of queued) drain(entry);
    queued.clear();
    flushScheduled = false;
    const ids = [...dirty];
    dirty.clear();
    const notifyStatus = statusDirty;
    statusDirty = false;
    for (const id of ids) {
      for (const listener of [...(listeners.get(id) ?? [])]) listener();
    }
    if (notifyStatus) for (const listener of [...statusListeners]) listener();
  };

  const scheduleFlush = () => {
    if (flushScheduled) return;
    flushScheduled = true;
    if (typeof requestAnimationFrame === "function" && !isHidden()) requestAnimationFrame(flush);
    else setTimeout(flush, 0);
  };

  const changed = (entry: Entry) => {
    dirty.add(entry.id);
    scheduleFlush();
  };

  const computeStatus = (): ConnectionStatus => {
    if (entries.size === 0) return "idle";
    if (locked) return "locked";
    if (connection?.authed && isOnline()) return "live";
    if (limited) return "limited";
    if (rateLimited) return "rate_limited";
    if (connection === null && (isHidden() || !isOnline())) return "paused";
    for (const entry of entries.values()) if (entry.state.value !== null) return "reconnecting";
    return "connecting";
  };

  const refreshStatus = () => {
    const next = computeStatus();
    if (next === currentStatus) return;
    currentStatus = next;
    statusDirty = true;
    scheduleFlush();
  };

  const pump = (current: Connection) => {
    clearTimeout(current.pump);
    current.pump = undefined;
    const socket = current.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (current.refilledAt !== null) {
      current.tokens = Math.min(COMMAND_BURST, current.tokens + (now - current.refilledAt) / COMMAND_REFILL_MS);
      current.refilledAt = now;
    }
    while (current.outbox.length > 0 && current.tokens >= 1) {
      current.tokens -= 1;
      socket.send(encodeCommand(current.outbox.shift()!));
    }
    if (current.outbox.length > 0 && current.refilledAt !== null) {
      current.pump = setTimeout(() => pump(current), (1 - current.tokens) * COMMAND_REFILL_MS);
    }
  };

  const send = (command: ClientCommand): boolean => {
    const current = connection;
    if (!current?.socket || current.socket.readyState !== WebSocket.OPEN) return false;
    current.outbox.push(command);
    pump(current);
    return true;
  };

  const subscribe = (entry: Entry) => {
    if (entry.subscribed || entry.state.phase === "ended") return;
    entry.subscribed = send({ type: "sub", topic: entry.topic });
  };

  const detachTopics = () => {
    for (const entry of entries.values()) {
      if (entry.kind === "whale") entry.queue = [];
      entry.subscribed = false;
      const rejected = entry.retried && entry.state.phase === "ended" && entry.state.value === null;
      entry.retried = false;
      if (entry.state.phase === "live" || rejected) {
        setPhase(entry, rejected ? "loading" : "resyncing", null);
        changed(entry);
      }
    }
  };

  const teardown = (current: Connection) => {
    clearTimeout(current.watchdog);
    clearTimeout(current.renewal);
    clearTimeout(current.stable);
    clearTimeout(current.pump);
    clearTimeout(current.ack);
    const socket = current.socket;
    if (!socket) return;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    if (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN) socket.close();
  };

  const dropConnection = () => {
    if (connection === null) return;
    teardown(connection);
    connection = null;
    detachTopics();
  };

  const backoff = () => {
    const ceiling = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** attempt);
    attempt += 1;
    return Math.random() * ceiling;
  };

  const scheduleReconnect = (delay: number) => {
    clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(connect, delay);
  };

  const lock = () => {
    locked = true;
    dropConnection();
    clearTimeout(reconnectTimer);
    reconnectTimer = undefined;
    refreshStatus();
  };

  const fail = (current: Connection, code: number | null) => {
    if (connection !== current) return;
    noteFirstLiveRow({ reconnect_ms: performance.now() });
    const fatal = current.fatal;
    dropConnection();
    const shutdown = code === 1012 || fatal === "server_shutdown";
    const reason = fatal ?? (code === null ? null : CLOSE_CODE_REASON[code] ?? null);
    if (code === 4030 || fatal === "subject_limit") limited = true;
    if (code === 4029 || fatal === "rate_limited") rateLimited = true;
    if (reason !== null && reason !== "server_shutdown") report(reason, null);
    if (shutdown) {
      scheduleReconnect(Math.random() * SHUTDOWN_JITTER_MS);
    } else {
      const delay = backoff();
      if (attempt >= REPEATED_RECONNECT_THRESHOLD) report("repeated_reconnect_failure", null);
      scheduleReconnect(delay);
    }
    refreshStatus();
  };

  const armWatchdog = (current: Connection) => {
    clearTimeout(current.watchdog);
    current.watchdog = setTimeout(() => fail(current, null), WATCHDOG_MS);
  };

  const renew = async (current: Connection) => {
    current.renewal = undefined;
    const session = await mintSession(sessionPath);
    if (connection !== current) return;
    if (session === "locked") {
      lock();
      return;
    }
    if (session === null) {
      if (Date.now() + RENEW_RETRY_MS < current.expiresAt) {
        current.renewal = setTimeout(() => void renew(current), RENEW_RETRY_MS);
      }
      return;
    }
    send({ type: "auth", ticket: session.ticket });
  };

  const subscribeAll = () => {
    for (const entry of entries.values()) {
      if (entry.kind === "whale" && entry.refs === 0 && entry.state.value !== null) remove(entry);
      else subscribe(entry);
    }
  };

  const authenticated = (current: Connection, expMs: number, serverNowMs: number) => {
    const lifetime = expMs - serverNowMs;
    current.expiresAt = Date.now() + lifetime;
    current.clockOffsetMs = Math.round((Date.now() - serverNowMs) / 1000) * 1000;
    clearTimeout(current.renewal);
    current.renewal = setTimeout(() => void renew(current), Math.max(0, lifetime - RENEW_BEFORE_MS));
    if (current.authed) return;
    noteFirstLiveRow({ auth_ms: performance.now() });
    current.authed = true;
    current.refilledAt = Date.now();
    pump(current);
    limited = false;
    rateLimited = false;
    current.stable = setTimeout(() => {
      attempt = 0;
    }, STABLE_MS);
    subscribeAll();
    refreshStatus();
  };

  const invalidTopicMessage = (topic: Topic) => {
    const entry = entries.get(topicId(topic));
    if (!entry || !entry.subscribed) return;
    if (entry.kind === "whale") drain(entry);
    if (entry.state.value !== null) setPhase(entry, "resyncing", null);
    if (!entry.retried) {
      entry.retried = true;
      report("invalid_topic_message", entry.topic);
      send({ type: "sub", topic: entry.topic });
    } else if (entry.state.value === null) {
      setPhase(entry, "ended", "unknown_topic");
    }
    changed(entry);
  };

  const revive = (entry: Entry) => {
    if (entry.kind !== "whale_row" || entry.state.phase !== "ended" || !REVIVABLE.has(entry.state.ended) || !isValidTopic(entry.topic)) return;
    setPhase(entry, entry.state.value === null ? "loading" : "resyncing", null);
    changed(entry);
    if (connection !== null) subscribe(entry);
  };

  const reviveListed = (messages: readonly TopicMessage[]) => {
    for (const entry of entries.values()) {
      if (entry.kind === "whale_row" && entry.refs > 0 && entry.state.phase === "ended" && listsRow(messages, entry.topic.id)) revive(entry);
    }
  };

  function drain(entry: WhaleEntry) {
    const messages = entry.queue;
    if (messages.length === 0) return;
    entry.queue = [];
    if (entries.get(entry.id) !== entry || !entry.subscribed) return;
    const reduction = reduceWhaleMessages(entry.state, messages);
    if (reduction.kind === "ignored") return;
    entry.state = reduction.state;
    if (reduction.kind === "resync") {
      reportResync(entry.topic, reduction.cause);
      send({ type: "sub", topic: entry.topic });
    } else if (messages.some((message) => message.type === "snap")) entry.retried = false;
    changed(entry);
    if (reduction.kind === "applied") reviveListed(messages);
  }

  const topicMessage = (message: TopicMessage) => {
    const entry = entries.get(topicId(message.topic));
    if (!entry || !entry.subscribed) return;
    if (entry.kind === "whale") {
      entry.queue.push(message);
      queued.add(entry);
      scheduleFlush();
      return;
    }
    const reduction = reduceEntry(entry, message);
    if (reduction.kind === "ignored") return;
    if (reduction.kind === "resync") {
      reportResync(entry.topic, reduction.cause);
      send({ type: "sub", topic: entry.topic });
    } else if (message.type === "snap") entry.retried = false;
    changed(entry);
  };

  const acknowledge = (current: Connection) => {
    if (current.ack !== undefined || !current.authed) return;
    current.ack = setTimeout(() => {
      current.ack = undefined;
      if (connection === current && current.socket?.readyState === WebSocket.OPEN) {
        current.socket.send(encodeCommand({ type: "ping", n: current.received }));
        current.acked = true;
      }
    }, ACK_MS);
  };

  const receive = (current: Connection, data: unknown) => {
    const receivedAtMs = performance.now();
    armWatchdog(current);
    current.received += 1;
    const text = typeof data === "string" ? data : "";
    let message: ServerMessage | null;
    try {
      message = parseServerMessage(text);
    } catch (error) {
      if (error instanceof ProtocolError && error.topic !== null) {
        invalidTopicMessage(error.topic);
        acknowledge(current);
      } else {
        report("invalid_message", null, undefined, invalidMessageTags(error, data, text.length, current.authed));
        fail(current, null);
      }
      return;
    }
    if (message?.type !== "pong") acknowledge(current);
    if (message === null) return;
    switch (message.type) {
      case "auth":
        authenticated(current, message.expMs, message.serverNowMs);
        acknowledge(current);
        return;
      case "snap":
      case "delta":
        if (message.type === "snap") noteLiveSnapshot(message.topic.kind, receivedAtMs, text.length);
        reportRejected(message);
        topicMessage(current.clockOffsetMs === 0 ? message : onLocalClock(message, current.clockOffsetMs));
        return;
      case "end": {
        const entry = entries.get(topicId(message.topic));
        if (!entry || !entry.subscribed) return;
        if (entry.kind === "whale") drain(entry);
        entry.subscribed = false;
        setPhase(entry, "ended", message.reason);
        changed(entry);
        return;
      }
      case "error":
        current.fatal = message.code;
        return;
      case "hb":
      case "pong":
        return;
    }
  };

  const open = async (current: Connection) => {
    const held = primed !== null && primed.expiresAtMs > Date.now() + PRIMED_MIN_LIFE_MS ? primed : null;
    primed = null;
    if (held !== null) usedTickets.add(held.ticket);
    const session = held ?? await mintSession(sessionPath);
    if (connection !== current) return;
    if (session === "locked") {
      lock();
      return;
    }
    if (session === null) {
      fail(current, null);
      return;
    }
    if (held === null) noteFirstLiveRow({ ticket_ms: performance.now() });
    const socket = takeEarlySocket(session.streamUrl) ?? new WebSocket(session.streamUrl);
    noteFirstLiveRow({ stream_host: new URL(session.streamUrl).host });
    current.socket = socket;
    const opened = () => {
      noteFirstLiveRow({ socket_open_ms: performance.now() });
      send({ type: "auth", ticket: session.ticket });
      subscribeAll();
    };
    socket.onopen = opened;
    socket.onmessage = (event: MessageEvent) => receive(current, event.data);
    socket.onclose = (event: CloseEvent) => fail(current, event.code);
    socket.onerror = () => {};
    if (socket.readyState === WebSocket.OPEN) opened();
  };

  function connect() {
    clearTimeout(reconnectTimer);
    reconnectTimer = undefined;
    if (connection !== null || entries.size === 0 || locked || isHidden() || !isOnline()) {
      refreshStatus();
      return;
    }
    const current: Connection = {
      socket: null, authed: false, fatal: null, expiresAt: 0, watchdog: undefined, renewal: undefined, stable: undefined, clockOffsetMs: 0,
      tokens: COMMAND_BURST, refilledAt: null, outbox: [], pump: undefined, received: 0, ack: undefined, acked: false,
    };
    connection = current;
    resyncWindowAt = -Infinity;
    noteFirstLiveRow({ live_start_ms: performance.now() });
    armWatchdog(current);
    refreshStatus();
    void open(current);
  }

  const visibilityChanged = () => {
    clearTimeout(hiddenTimer);
    hiddenTimer = undefined;
    if (isHidden()) {
      hiddenTimer = setTimeout(() => {
        hiddenTimer = undefined;
        clearTimeout(reconnectTimer);
        reconnectTimer = undefined;
        dropConnection();
        refreshStatus();
      }, HIDDEN_CLOSE_MS);
      return;
    }
    if (connection !== null || reconnectTimer !== undefined) {
      refreshStatus();
      return;
    }
    if (limited || rateLimited) {
      scheduleReconnect(backoff());
      refreshStatus();
      return;
    }
    connect();
  };

  const wentOnline = () => {
    attempt = 0;
    if (connection === null) connect();
  };

  const wentOffline = () => {
    clearTimeout(reconnectTimer);
    reconnectTimer = undefined;
    dropConnection();
    refreshStatus();
  };

  const attachDom = () => {
    if (domAttached || typeof window === "undefined") return;
    domAttached = true;
    document.addEventListener("visibilitychange", visibilityChanged);
    window.addEventListener("online", wentOnline);
    window.addEventListener("offline", wentOffline);
  };

  const detachDom = () => {
    if (!domAttached) return;
    domAttached = false;
    document.removeEventListener("visibilitychange", visibilityChanged);
    window.removeEventListener("online", wentOnline);
    window.removeEventListener("offline", wentOffline);
  };

  const idle = () => {
    if (connection !== null) teardown(connection);
    connection = null;
    clearTimeout(reconnectTimer);
    clearTimeout(hiddenTimer);
    reconnectTimer = undefined;
    hiddenTimer = undefined;
    attempt = 0;
    locked = false;
    limited = false;
    rateLimited = false;
    detachDom();
    refreshStatus();
  };

  const remove = (entry: Entry) => {
    clearTimeout(entry.linger);
    entry.linger = undefined;
    if (entries.get(entry.id) !== entry) return;
    entries.delete(entry.id);
    if (entry.kind === "whale") entry.queue = [];
    if (entry.subscribed) send({ type: "unsub", topic: entry.topic });
    entry.subscribed = false;
    changed(entry);
    if (entries.size === 0) idle();
    else refreshStatus();
  };

  const evict = (group: CapGroup) => {
    let count = 0;
    let oldest: Entry | undefined;
    for (const entry of entries.values()) {
      if (entry.kind === "catalog" || CAP_GROUP[entry.kind] !== group) continue;
      count += 1;
      if (entry.refs === 0 && (oldest === undefined || entry.used < oldest.used)) oldest = entry;
    }
    if (count >= LIMITS[group] && oldest !== undefined) remove(oldest);
  };

  const add = (topic: Topic): Entry => {
    if (topic.kind !== "catalog") evict(CAP_GROUP[topic.kind]);
    const entry = createEntry(topic);
    entries.set(entry.id, entry);
    attachDom();
    changed(entry);
    if (connection !== null) subscribe(entry);
    else if (reconnectTimer === undefined) connect();
    refreshStatus();
    return entry;
  };

  const hold = (topic: Topic, lingerEmpty: boolean) => {
    const entry = entries.get(topicId(topic)) ?? add(topic);
    if (entry.refs === 0) revive(entry);
    entry.refs += 1;
    entry.used = ++clock;
    clearTimeout(entry.linger);
    entry.linger = undefined;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      entry.refs -= 1;
      entry.used = ++clock;
      if (entry.refs > 0) return;
      const linger = entry.state.value === null && !lingerEmpty ? 0 : LINGER_MS[entry.kind];
      entry.linger = setTimeout(() => remove(entry), linger);
    };
  };

  const read = <T extends Topic>(topic: T): TopicState<TopicValue<T>> =>
    (entries.get(topicId(topic))?.state ?? EMPTY_STATE) as TopicState<TopicValue<T>>;

  const readBoardView = (topic: BoardTopic): BoardView => {
    const entry = entries.get(topicId(topic));
    return entry?.kind === "board" ? entry.view : EMPTY_VIEW;
  };

  const readPropsView = (topic: BookRowsTopic): PropsView => {
    const entry = entries.get(topicId(topic));
    return entry?.kind === "props" || entry?.kind === "optimizer" ? entry.view : EMPTY_PROPS_VIEW;
  };

  const readDiscrepanciesView = (topic: DiscrepanciesTopic): DiscrepanciesView => {
    const entry = entries.get(topicId(topic));
    return entry?.kind === "discrepancies" ? entry.view : EMPTY_DISCREPANCIES_VIEW;
  };

  const readArbitrageView = (topic: ArbitrageTopic): ArbitrageView => {
    const entry = entries.get(topicId(topic));
    return entry?.kind === "arbitrage" ? entry.view : EMPTY_ARBITRAGE_VIEW;
  };

  const readSniperView = (topic: SniperRowsTopic): SniperView => {
    const entry = entries.get(topicId(topic));
    return entry?.kind === "sniper" || entry?.kind === "sniper_live" ? entry.view : EMPTY_SNIPER_VIEW;
  };

  const readLowHoldView = (topic: LowHoldTopic): LowHoldView => {
    const entry = entries.get(topicId(topic));
    return entry?.kind === "lowhold" ? entry.view : EMPTY_LOWHOLD_VIEW;
  };

  const listBoards = (): readonly HeldBoard[] => {
    const held: HeldBoard[] = [];
    for (const entry of entries.values()) {
      if (entry.kind === "board" && entry.state.value !== null) held.push({ topic: entry.topic, view: entry.view });
    }
    return held;
  };

  const listen = (topic: Topic, onChange: () => void) => {
    const id = topicId(topic);
    const set = listeners.get(id) ?? new Set<() => void>();
    listeners.set(id, set);
    set.add(onChange);
    return () => {
      set.delete(onChange);
      if (set.size === 0 && listeners.get(id) === set) listeners.delete(id);
    };
  };

  return {
    retain: (topic) => hold(topic, false),
    prefetch: (topic) => hold(topic, true)(),
    read,
    readBoardView,
    readPropsView,
    readDiscrepanciesView,
    readSniperView,
    readArbitrageView,
    readLowHoldView,
    listBoards,
    listen,
    prime: (session) => {
      const expiresAtMs = Date.parse(session.expires_at);
      if (!usedTickets.has(session.ticket) && expiresAtMs > Date.now() + PRIMED_MIN_LIFE_MS) primed = { ticket: session.ticket, streamUrl: session.stream_url, expiresAtMs };
    },
    status: () => currentStatus,
    listenStatus: (onChange) => {
      statusListeners.add(onChange);
      return () => {
        statusListeners.delete(onChange);
      };
    },
    report,
  };
}

const holder = globalThis as typeof globalThis & {
  __upsideOddsLiveClient?: OddsLiveClient;
  __upsideFantasyLiveClient?: OddsLiveClient;
  __upsideOptimizerLiveClient?: OddsLiveClient;
  __upsideDiscrepanciesLiveClient?: OddsLiveClient;
  __upsideArbitrageLiveClient?: OddsLiveClient;
  __upsideLowHoldLiveClient?: OddsLiveClient;
  __upsideWhaleLiveClient?: OddsLiveClient;
  __upsideSniperLiveClient?: OddsLiveClient;
};

export function getOddsLiveClient(): OddsLiveClient {
  holder.__upsideOddsLiveClient ??= createOddsLiveClient();
  return holder.__upsideOddsLiveClient;
}

export function getFantasyLiveClient(): OddsLiveClient {
  holder.__upsideFantasyLiveClient ??= createOddsLiveClient(FANTASY_SESSION_PATH);
  return holder.__upsideFantasyLiveClient;
}

export function getOptimizerLiveClient(): OddsLiveClient {
  holder.__upsideOptimizerLiveClient ??= createOddsLiveClient(OPTIMIZER_SESSION_PATH);
  return holder.__upsideOptimizerLiveClient;
}

export function getDiscrepanciesLiveClient(): OddsLiveClient {
  holder.__upsideDiscrepanciesLiveClient ??= createOddsLiveClient(DISCREPANCIES_SESSION_PATH);
  return holder.__upsideDiscrepanciesLiveClient;
}

export function getArbitrageLiveClient(): OddsLiveClient {
  holder.__upsideArbitrageLiveClient ??= createOddsLiveClient(ARBITRAGE_SESSION_PATH);
  return holder.__upsideArbitrageLiveClient;
}

export function getLowHoldLiveClient(): OddsLiveClient {
  holder.__upsideLowHoldLiveClient ??= createOddsLiveClient(LOW_HOLD_SESSION_PATH);
  return holder.__upsideLowHoldLiveClient;
}

export function getSniperLiveClient(): OddsLiveClient {
  holder.__upsideSniperLiveClient ??= createOddsLiveClient(SNIPER_SESSION_PATH);
  return holder.__upsideSniperLiveClient;
}

export function getWhaleLiveClient(): OddsLiveClient {
  holder.__upsideWhaleLiveClient ??= createOddsLiveClient(WHALE_SESSION_PATH);
  return holder.__upsideWhaleLiveClient;
}
